import React, { useRef, useState } from 'react';
import { LuMail, LuCheck } from 'react-icons/lu';
import Card from '../../../components/ui/Card/Card';
import Button from '../../../components/ui/Button/Button';
import TextField from '../../../components/ui/TextField/TextField';
import { sendSessionReceiptEmail } from '../../../api/sessionsApi';
import { useAuth } from '../../../context/AuthContext';
import {
  deriveRecipientName,
  emailFieldError,
  nameFieldError,
  isValidRecipientName,
} from '../../../utils/receiptRecipient';
import './ReceiptEmailCard.less';

/**
 * "Email me a receipt" for one session.
 *
 *  - Address: the account email by default; "Change email" reveals an input.
 *  - Name: taken from the profile (first + last, trimmed; a single-token name is
 *    fine). Only when the profile has no usable name do we ask for one inline.
 *  - Both are validated before the request, with field-level messages.
 *  - One request at a time (ref guard, not just disabled state), with sending /
 *    sent / failed / throttled states and a retry that keeps what was typed.
 */
function ReceiptEmailCard({ sessionUid }) {
  const { user } = useAuth();
  const accountEmail = typeof user?.email === 'string' ? user.email.trim() : '';
  const hasAccountEmail = !!accountEmail;
  const profileName = deriveRecipientName(user);
  const hasProfileName = isValidRecipientName(profileName);

  const [overriding, setOverriding] = useState(false);
  const [email, setEmail] = useState('');
  const [typedName, setTypedName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const inFlight = useRef(false);
  const emailRef = useRef(null);
  const nameRef = useRef(null);

  const showEmailInput = overriding || !hasAccountEmail;
  const chosenEmail = showEmailInput ? email.trim() : accountEmail;
  const chosenName = hasProfileName ? profileName : typedName.trim();
  const sent = !!sentTo && sentTo === chosenEmail;

  function clearOutcome() {
    setFormError(null);
    setNotice(null);
    setSentTo(null);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (inFlight.current || sent) return;

    const errs = {};
    const emailErr = emailFieldError(chosenEmail);
    if (emailErr) errs.email = emailErr;
    if (!hasProfileName) {
      const nameErr = nameFieldError(typedName);
      if (nameErr) errs.fullName = nameErr;
    }
    setFieldErrors(errs);
    setFormError(null);
    setNotice(null);
    if (errs.email || errs.fullName) {
      // Account email can't be wrong here (no input on screen); only focus
      // fields that exist.
      (errs.email && showEmailInput ? emailRef : nameRef).current?.focus();
      return;
    }

    inFlight.current = true;
    setSending(true);
    try {
      const res = await sendSessionReceiptEmail(sessionUid, {
        email: chosenEmail,
        fullName: chosenName,
      });
      if (res?.ok && res.throttled) {
        setNotice('A receipt for this session was sent a moment ago. Please wait a minute before sending it again.');
      } else if (res?.ok) {
        setSentTo(chosenEmail);
      } else if (res?.field === 'email') {
        setFieldErrors({ email: res.message });
      } else if (res?.field === 'fullName') {
        setFieldErrors({ fullName: res.message });
      } else {
        setFormError(res?.message || 'Could not email the receipt. Please try again.');
      }
    } catch {
      setFormError('Could not email the receipt. Please try again.');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  function openOverride() {
    setEmail(hasAccountEmail ? accountEmail : '');
    setFieldErrors({});
    clearOutcome();
    setOverriding(true);
  }

  function cancelOverride() {
    setEmail('');
    setFieldErrors({});
    clearOutcome();
    setOverriding(false);
  }

  const failed = !!formError || !!fieldErrors.email || !!fieldErrors.fullName;
  const submitLabel = sent ? 'Receipt sent' : failed ? 'Try again' : 'Send receipt';

  return (
    <Card>
      <div className="gc-receipt-card">
        <div className="gc-receipt-card-head">
          <h3 id="gc-receipt-card-title" className="gc-detail-email-title">Email me a receipt</h3>
          {showEmailInput && (
            <p id="gc-receipt-card-sub" className="gc-detail-email-sub">
              We'll send a copy of this receipt to the email you enter below.
            </p>
          )}
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="gc-receipt-form"
          aria-labelledby="gc-receipt-card-title"
        >
          {showEmailInput ? (
            <TextField
              ref={emailRef}
              label="Email address"
              type="email"
              name="receipt-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((f) => ({ ...f, email: undefined }));
                setFormError(null);
                setNotice(null);
              }}
              onBlur={() => {
                if (email.trim()) setFieldErrors((f) => ({ ...f, email: emailFieldError(email) || undefined }));
              }}
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              error={fieldErrors.email}
              errorRole="alert"
              disabled={sending}
              required
            />
          ) : (
            <p className="gc-detail-email-default">
              <LuCheck aria-hidden="true" className="gc-detail-email-default-icon" />
              <span>
                <span className="gc-detail-email-default-label">Send receipt to:</span>
                {' '}
                <span className="gc-detail-email-default-value">{accountEmail}</span>
              </span>
            </p>
          )}

          {!hasProfileName && (
            <TextField
              ref={nameRef}
              label="Your name"
              name="receipt-name"
              value={typedName}
              onChange={(e) => {
                setTypedName(e.target.value);
                setFieldErrors((f) => ({ ...f, fullName: undefined }));
                setFormError(null);
              }}
              autoComplete="name"
              hint="Printed on the receipt. Add it to your profile to skip this step."
              error={fieldErrors.fullName}
              errorRole="alert"
              disabled={sending}
              required
            />
          )}

          <div className="gc-receipt-messages">
            {formError && (
              <p className="gc-detail-email-form-error" role="alert">{formError}</p>
            )}
            {notice && (
              <p className="gc-detail-email-notice" role="status">{notice}</p>
            )}
            {sent && (
              <p className="gc-detail-email-success" role="status">
                <LuCheck aria-hidden="true" />
                <span>Receipt sent to {sentTo}.</span>
              </p>
            )}
          </div>

          <div className="gc-detail-email-actions">
            {hasAccountEmail && (
              <Button
                type="button"
                variant="ghost"
                onClick={showEmailInput ? cancelOverride : openOverride}
                disabled={sending}
              >
                {showEmailInput ? 'Use my account email' : 'Change email'}
              </Button>
            )}
            <Button
              type="submit"
              busy={sending}
              disabled={sent}
              leadingIcon={sent ? <LuCheck /> : <LuMail />}
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
}

export default ReceiptEmailCard;
